import { useState, useEffect, useMemo } from 'react';
import { gql, useLazyQuery, useSubscription } from '@apollo/client';
import { Box, Typography, Button } from '@mui/material';
import { IActivity } from '../../types/activity';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import StandardTable from '../tables/StandardTable';
import { convertAllDates, downloadUnitsSharedExcel, numToCurrency, netHST } from '../../utils/Functions';
import { FlexBetween } from '../../commonStyles';

const Activity = () => {
  const project = useSelector(selectProject);

  const [activities, setActivities] = useState<IActivity[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [count, setCount] = useState<number>(0);

  const [getActivities, { loading }] = useLazyQuery(GETACTIVITIES, {
    onCompleted: (data) => {
      setActivities(data.activityPagination.items);
      setCount(data.activityPagination.count);
    },
  });

  const [getUnitsSharedActivities, { loading: unitsSharedLoading }] = useLazyQuery(GETUNITSSHAREDACTIVITIES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      let rows = data.activityMany.flatMap((activity: any) =>
        (activity.units || []).map((unit: any) => ({
          user: activity.user.fullName,
          suite: unit.suite,
          date: convertAllDates(activity.timestamp, 'PPpp'),
          title: activity.title,
          content: activity.content,
          unitType: unit.unitType,
          modelType: unit.modelType,
          exposure: unit.exposure,
          bathroom: unit.bathroom,
          size: unit.size,
          level: unit.level,
          ceilingHeight: unit.ceilingHeight,
          basePrice: numToCurrency.format(project.netHst ? netHST(unit.basePrice, project) : unit.basePrice),
          // Extra fields, not part of the visible Units Shared columns, used to build the Counts tab.
          unitId: unit._id,
          rawBasePrice: unit.basePrice,
          timestamp: activity.timestamp,
        }))
      );

      let headers = [
        { label: 'User', id: 'user' },
        { label: 'Suite', id: 'suite' },
        { label: 'Date', id: 'date' },
        { label: 'Title', id: 'title' },
        { label: 'Content', id: 'content' },
        { label: 'Unit Type', id: 'unitType' },
        { label: 'Model Type', id: 'modelType' },
        { label: 'Exposure', id: 'exposure' },
        { label: 'Bathroom', id: 'bathroom' },
        { label: 'Size', id: 'size' },
        { label: 'Level', id: 'level' },
        { label: 'Ceiling Height', id: 'ceilingHeight' },
        { label: 'Base Price', id: 'basePrice' },
      ];

      let widths = {
        user: 20,
        suite: 15,
        date: 20,
        title: 25,
        content: 60,
        unitType: 15,
        modelType: 15,
        exposure: 15,
        bathroom: 12,
        size: 12,
        level: 12,
        ceilingHeight: 15,
        basePrice: 15,
      };

      downloadUnitsSharedExcel(rows, headers, widths, project);
    },
  });

  useSubscription(ACTIVITYSUBSCRIPTION, {
    variables: { projectId: project._id },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setActivities([data.logActivity, ...activities]);
    },
  });

  useEffect(() => {
    getActivities({ variables: { filter: { project: project._id }, page: pageNumber, perPage: 25, sort: '_ID_DESC' } });
  }, [pageNumber, getActivities]);

  const handleGlobalFilterValue = (value: string) => {
    setPageNumber(1);
    getActivities({ variables: { filter: { project: project._id, search: value }, page: 1, perPage: 25, sort: '_ID_DESC' } });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Title',
        accessor: (rowData: any) => rowData.title,
      },
      {
        Header: 'Content',
        accessor: (rowData: any) => rowData.content,
      },
      {
        Header: 'Deal',
        accessor: (rowData: any) => (rowData.deal ? rowData.deal.unit.suite : 'N/A'),
      },
      {
        Header: 'User',
        accessor: (rowData: any) => rowData.user.fullName,
      },
      {
        Header: 'Date Created',
        accessor: (rowData: any) => convertAllDates(rowData.timestamp, 'PPpp'),
      },
    ];
  }, []);
  
  return (
    <Box sx={{ p: 3 }}>
      <FlexBetween sx={{ mb: 2 }}>
        <Typography variant={'h5'}>
          <strong>Activities</strong>
        </Typography>
        <Button
          variant="contained"
          disabled={unitsSharedLoading}
          onClick={() =>
            getUnitsSharedActivities({ variables: { filter: { project: project._id, titlePrefix: 'Units Shared' }, limit: 10000 } })
          }
        >
          Download Units Shared
        </Button>
      </FlexBetween>
      <>
        <StandardTable
          columns={columns}
          data={activities}
          handleGlobalFilterValue={handleGlobalFilterValue}
          loading={loading}
          count={count}
        />
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>
            {'<'}
          </Button>
          <span>{pageNumber}</span>
          <Button disabled={pageNumber === Math.ceil(count / 15)} onClick={() => setPageNumber(pageNumber + 1)}>
            {'>'}
          </Button>
        </Box>
      </>
    </Box>
  );
};

const GETACTIVITIES = gql`
  query activityPagination($filter: FilterFindManyActivityInput, $page: Int!, $perPage: Int!, $sort: SortFindManyActivityInput) {
    activityPagination(filter: $filter, page: $page, perPage: $perPage, sort: $sort) {
      items {
        project {
          _id
        }
        user {
          fullName
        }
        deal {
          _id
          unit {
            suite
          }
        }
        title
        content
        timestamp
      }
      count
    }
  }
`;

const GETUNITSSHAREDACTIVITIES = gql`
  query activityMany($filter: FilterFindManyActivityInput, $limit: Int) {
    activityMany(filter: $filter, limit: $limit) {
      user {
        fullName
      }
      timestamp
      title
      content
      units {
        _id
        suite
        unitType
        modelType
        exposure
        bathroom
        size
        level
        ceilingHeight
        basePrice
      }
    }
  }
`;

const ACTIVITYSUBSCRIPTION = gql`
  subscription logActivity($projectId: String!) {
    logActivity(projectId: $projectId) {
      _id
      user {
        firstName
        lastName
      }
      deal {
        unit {
          suite
        }
        purchasers {
          firstName
          lastName
        }
      }
      title
      content
      timestamp
    }
  }
`;

export default Activity;
